import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Check, ChevronLeft, ChevronRight, List } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/core/DropdownMenu';
import { IconButton } from '@/shared/ui/core/IconButton';
import { WithTooltip } from '@/shared/ui/core/Tooltip';

export type TabItem = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
};

export type TabsProps = {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children?: ReactNode;
  className?: string;
  listClassName?: string;
  /** Card around the tab strip, matching the detail reference. Panels stay outside. */
  frameList?: boolean;
};

/** Hide native scrollbar — overflow is driven by arrow controllers. */
const scrollHideClass =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  children,
  className,
  listClassName,
  frameList = false,
}: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const fallback = defaultValue ?? items[0]?.value ?? '';
  const [uncontrolled, setUncontrolled] = useState(fallback);
  const active = value ?? uncontrolled;

  const handleValueChange = (next: string) => {
    if (value === undefined) setUncontrolled(next);
    onValueChange?.(next);
  };

  const updateScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setCanPrev(false);
      setCanNext(false);
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    const overflow = max > 1;
    setCanPrev(overflow && el.scrollLeft > 1);
    setCanNext(overflow && el.scrollLeft < max - 1);
  }, []);

  useLayoutEffect(() => {
    updateScrollState();
  }, [items, active, updateScrollState]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => updateScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [updateScrollState]);

  /** Keep active tab visible when selection / layout changes. */
  useEffect(() => {
    const el = listRef.current;
    if (!el || !active) return;
    const trigger = el.querySelector<HTMLElement>(
      `[role="tab"][data-state="active"]`,
    );
    trigger?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    const t = window.setTimeout(updateScrollState, 200);
    return () => window.clearTimeout(t);
  }, [active, updateScrollState]);

  const scrollByDir = (dir: -1 | 1) => {
    const el = listRef.current;
    if (!el) return;
    const delta = Math.max(120, Math.floor(el.clientWidth * 0.75)) * dir;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const showScrollControls = !frameList && (canPrev || canNext);
  // When tabs overflow (common on phone), expose every tab in a menu — e.g. "Xem trước In".
  const showAllMenu = !frameList && items.length > 3 && (canPrev || canNext);

  return (
    <TabsPrimitive.Root
      value={active}
      onValueChange={handleValueChange}
      className={cn('w-full', className)}
    >
      <div
        className={cn(
          "flex items-stretch border-b border-border",
          frameList &&
            "rounded-xl border border-border/80 bg-surface-raised p-1 shadow-xs border-b-0",
        )}
      >
        {showScrollControls ? (
          <div className="flex shrink-0 items-center border-r border-border">
            <IconButton
              label="Cuộn tab trái"
              icon={<ChevronLeft className="size-4" aria-hidden />}
              size="sm"
              variant="ghost"
              disabled={!canPrev}
              onClick={() => scrollByDir(-1)}
              className="rounded-none"
            />
          </div>
        ) : null}

        <TabsPrimitive.List
          ref={listRef}
          className={cn(
            'flex min-w-0 flex-1 gap-1 overflow-x-auto overscroll-x-contain',
            scrollHideClass,
            listClassName,
          )}
        >
          {items.map((item) => (
            <TabsPrimitive.Trigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className={cn(
                'inline-flex items-center gap-1.5',
                frameList
                  ? 'shrink-0 rounded-lg px-3.5 py-2 text-caption font-semibold select-none cursor-pointer transition-all text-text-secondary hover:text-text hover:bg-surface/60 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-xs data-[state=active]:font-bold'
                  : 'shrink-0 rounded-t-control border-b-2 border-transparent px-3 py-2.5 text-body font-semibold text-text-secondary cursor-pointer transition-all duration-150 hover:border-primary/40 hover:bg-primary-soft/40 hover:text-primary active:bg-primary-soft/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:hover:border-primary data-[state=active]:hover:bg-primary-soft/20 data-[state=active]:hover:text-primary',
                'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-transparent disabled:hover:bg-transparent disabled:hover:text-text-secondary',
              )}
            >
              {item.icon ? (
                <span className="inline-flex shrink-0 [&_svg]:size-4" aria-hidden>
                  {item.icon}
                </span>
              ) : null}
              {item.label}
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>

        {showScrollControls ? (
          <div className="flex shrink-0 items-center border-l border-border">
            <IconButton
              label="Cuộn tab phải"
              icon={<ChevronRight className="size-4" aria-hidden />}
              size="sm"
              variant="ghost"
              disabled={!canNext}
              onClick={() => scrollByDir(1)}
              className="rounded-none"
            />
          </div>
        ) : null}

        {showAllMenu ? (
          <div className="flex shrink-0 items-center border-l border-border bg-bg/60">
            <DropdownMenu>
              <WithTooltip label="Tất cả tab">
                <DropdownMenuTrigger asChild>
                  <IconButton
                    tooltip={false}
                    label="Tất cả tab"
                    icon={<List className="size-4" aria-hidden />}
                    size="sm"
                    variant="ghost"
                    className="rounded-none"
                  />
                </DropdownMenuTrigger>
              </WithTooltip>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                {items.map((item) => {
                  const selected = item.value === active;
                  return (
                    <DropdownMenuItem
                      key={item.value}
                      disabled={item.disabled}
                      onSelect={() => handleValueChange(item.value)}
                      className={cn(
                        selected && 'bg-primary-soft font-semibold text-primary',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {selected ? (
                        <Check className="size-3.5 shrink-0 text-primary" aria-hidden />
                      ) : (
                        <span className="size-3.5 shrink-0" aria-hidden />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>
      {children}
    </TabsPrimitive.Root>
  );
}

export function TabPanel({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <TabsPrimitive.Content
      value={value}
      className={cn(
        'mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
        className,
      )}
    >
      {children}
    </TabsPrimitive.Content>
  );
}
